import React from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import * as ProcessActions from "../../redux/actions/process.actions";
import ApiClient from "../../api/ApiClient";
import { getUserInfo } from "../../utils/Functions";

function IconInit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}
function IconChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SectionLabel({ title, subtitle }) {
  return (
    <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 1 }}>{subtitle}</div>}
    </div>
  );
}

function SelectCard({ options, value, onChange, labelKey, valueKey, loading }) {
  return (
    <div style={{ padding: "8px 16px 4px" }}>
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", color: "#9ca3af", fontSize: 12 }}>
          <div style={{ width: 14, height: 14, border: "2px solid #e5e7eb", borderTopColor: "#0891b2", borderRadius: "50%", animation: "dlg-spin 0.7s linear infinite" }} />
          Cargando...
        </div>
      ) : options.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 12, padding: "8px 0" }}>Sin opciones disponibles</div>
      ) : options.map(opt => {
        const val = opt[valueKey];
        const label = opt[labelKey];
        const isSelected = value === val;
        return (
          <div
            key={val}
            onClick={() => onChange(val)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 4, cursor: "pointer", background: isSelected ? "#e0f2fe" : "#f9fafb", border: `1.5px solid ${isSelected ? "#0891b2" : "transparent"}`, transition: "all 0.12s", userSelect: "none" }}
            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "#f9fafb"; }}
          >
            <div style={{ width: 18, height: 18, border: `2px solid ${isSelected ? "#0891b2" : "#d1d5db"}`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "border-color 0.12s" }}>
              {isSelected && <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#0891b2" }} />}
            </div>
            <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? "#0369a1" : "#374151" }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function InitPanel({ onClose, id, code, header, selectedRecord, flowVars, dispatch }) {
  const [versions, setVersions] = React.useState([]);
  const [offices, setOffices] = React.useState([]);
  const [version, setVersion] = React.useState(null);
  const [office, setOffice] = React.useState(null);
  const [params, setParams] = React.useState([]);
  const [loadingVersions, setLoadingVersions] = React.useState(true);
  const [loadingOffices, setLoadingOffices] = React.useState(true);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  React.useEffect(() => {
    if (flowVars && flowVars.length > 0) {
      setParams(flowVars.map(v => ({ ...v })));
    }
  }, [flowVars]);

  React.useEffect(() => {
    setLoadingVersions(true);
    ApiClient.doPost("/flows/get-flow-versions", { id })
      .then(res => {
        const list = (res.result || []).map(r => ({ version: r.VERSION, versionlabel: "Versión " + r.VERSION, definition: r.FLOWDEFINITION }));
        setVersions(list);
        if (list.length > 0) setVersion(list[0].version);
      })
      .catch(() => {})
      .finally(() => setLoadingVersions(false));

    setLoadingOffices(true);
    ApiClient.doPost("/organizations/get-all", {})
      .then(res => {
        const list = res.result || [];
        setOffices(list);
        if (list.length > 0) setOffice(list[0].CODE);
      })
      .catch(() => {})
      .finally(() => setLoadingOffices(false));
  }, [id]);

  const entityLabel = React.useMemo(() => {
    if (!selectedRecord) return String(id);
    const vals = Object.values(selectedRecord);
    return vals.length > 1 ? String(vals[1] || id) : String(id);
  }, [selectedRecord, id]);

  const updateParam = (idx, val) => {
    setParams(prev => prev.map((p, i) => i === idx ? { ...p, DEFVALUE: val } : p));
  };

  const handleSave = () => {
    const mappedParams = params.map(p => ({ ...p, var: p.VARNAME, value: p.DEFVALUE }));
    dispatch(ProcessActions.processData("flows", "init-flow", {
      organization: getUserInfo().ORGANIZATIONCODE,
      flowcode: code,
      officecode: office,
      params: mappedParams,
    }, header));
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const canSubmit = version != null && office != null;

  return ReactDOM.createPortal(
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, opacity: visible ? 1 : 0, transition: "opacity 0.22s ease" }} />

      <div style={{ position: "fixed", top: 0, right: 0, width: 460, height: "100vh", background: "#fff", zIndex: 9999, display: "flex", flexDirection: "column", boxShadow: "-6px 0 40px rgba(0,0,0,0.18)", fontFamily: '"72","72full",Arial,Helvetica,sans-serif', transform: visible ? "translateX(0)" : "translateX(100%)", transition: "transform 0.24s cubic-bezier(0.4,0,0.2,1)" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0d1117 0%, #1e1b4b 100%)", color: "#f1f5f9", padding: "20px 20px 16px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IconInit />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>Iniciar Flujo</div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2, letterSpacing: 0.3 }}>Configurar versión, departamento y parámetros</div>
              </div>
            </div>
            <button onClick={handleClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "5px 6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IconClose />
            </button>
          </div>

          <div style={{ marginTop: 14, background: "rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, background: "rgba(255,255,255,0.22)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {String(entityLabel).charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.65, letterSpacing: 0.5, textTransform: "uppercase" }}>Flujo</div>
              <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{entityLabel}</div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto" }}>

          {/* Section: Version */}
          <SectionLabel title="1. Versión" subtitle="Selecciona la versión del flujo a ejecutar" />
          <SelectCard
            options={versions.map(v => ({ CODE: v.version, NAME: v.versionlabel }))}
            value={version}
            onChange={setVersion}
            labelKey="NAME"
            valueKey="CODE"
            loading={loadingVersions}
          />

          {/* Section: Department */}
          <SectionLabel title="2. Departamento" subtitle="Selecciona el departamento que ejecuta el flujo" />
          <SelectCard
            options={offices}
            value={office}
            onChange={setOffice}
            labelKey="NAME"
            valueKey="CODE"
            loading={loadingOffices}
          />

          {/* Section: Parameters */}
          {params.length > 0 && (
            <>
              <SectionLabel title="3. Parámetros" subtitle="Valores iniciales de las variables del flujo" />
              <div style={{ padding: "8px 16px 12px" }}>
                {params.map((p, idx) => (
                  <div key={p.VARNAME} style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {p.VARLABEL || p.VARNAME}
                    </label>
                    <input
                      type="text"
                      value={p.DEFVALUE || ""}
                      onChange={e => updateParam(idx, e.target.value)}
                      placeholder={p.DEFVALUE || "—"}
                      style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1.5px solid #e5e7eb", borderRadius: 7, fontSize: 13, outline: "none", color: "#111827", background: "#fff" }}
                      onFocus={e => { e.target.style.borderColor = "#10b981"; }}
                      onBlur={e => { e.target.style.borderColor = "#e5e7eb"; }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 10, justifyContent: "flex-end", background: "#fff", flexShrink: 0 }}>
          <button
            onClick={handleClose}
            style={{ padding: "8px 20px", border: "1.5px solid #d1d5db", borderRadius: 7, background: "#fff", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#9ca3af"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSubmit}
            style={{ padding: "8px 22px", border: "none", borderRadius: 7, background: canSubmit ? "#047857" : "#a7f3d0", color: "#fff", cursor: canSubmit ? "pointer" : "default", fontSize: 13, fontWeight: 600 }}
          >
            Iniciar flujo
          </button>
        </div>
      </div>

      <style>{`@keyframes dlg-spin { to { transform: rotate(360deg); } }`}</style>
    </>,
    document.body
  );
}

const DialogInitFlowTemplate = (props) => {
  const { rows } = useSelector(state => state.Process);
  const dispatch = useDispatch();

  if (!props.status || props.id < 0) return null;

  return (
    <InitPanel
      onClose={props.close}
      id={props.id}
      code={props.code}
      header={props.header}
      selectedRecord={props.selectedRecord}
      flowVars={rows || []}
      dispatch={dispatch}
    />
  );
};

export default DialogInitFlowTemplate;
